import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { DndContext, DragOverlay, MeasuringStrategy } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { TaskEntry } from '../TaskEntry'
import { SelectionHullScope, useRestAfterDrop } from '../SelectionHull'
import { SectionDropZone } from '../SectionDropZone'
import { TaskRowDataScope } from '../TaskRowData'
import { LazyRowScope } from '../LazyRows'
import { ProjectSegment } from '@/src/renderer/features/taskManagement/pages/ProjectPage/components/ProjectSegment'
import { DragOverlayContent } from '@/src/renderer/features/taskManagement/pages/ProjectPage/components/DragOverlayContent'
import type { TaskBoardProps } from './TaskBoard.types'
import { SubtaskDragOutContext } from '@/src/renderer/features/taskManagement/utils/subtaskDragOut'
import { NestMenuContext } from '@/src/renderer/features/taskManagement/utils/nestMenu'
import { ListFocusContext } from '@/src/renderer/features/taskManagement/utils/listFocus'

/**
 * TaskBoard — reusable task board rendering.
 *
 * Renders the hover zone, drag-and-drop context, sortable segments, and drag
 * overlay that ProjectPage and QuickNotesPage share. The host page provides
 * data (tasks, sections, segments) and handlers via props. TaskBoard stays
 * scope-agnostic: it has no knowledge of projects vs quick-notes.
 */
export const TaskBoard: React.FC<TaskBoardProps> = ({
  segments, tasks, projectId,
  gapAfterSegmentIdx, gapHeight,
  activeDragId, draggedIds, dragCount, isCrossSegmentDrag, nestTarget, subtaskDragOut = null, insertLine = null, nestMenu = null,
  activeSectionDragId, dragSourceSegIdx, sectionDragWidth, dragRotation, sectionDragYOffset,
  taskListRef, taskListHandleRef, getSegmentHandleRef,
  sensors, customCollisionDetection, allSortableIds, segmentAwareStrategy,
  composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
  navigation,
  setHoveredSegmentIndex,
  creatingSegmentIdx, creatingPosition, creatingAfterTaskId, pendingInlineTarget,
  creationKey, creationLanded = false, newTaskDrop = null, inlineResetKey, skipInlineEntrance, autoSelectId,
  morphData, positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey,
  topCreateRef, bottomCreateRef,
  handleCreateTask, handleCreateTaskAndContinue,
  handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
  handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
  autoEditSectionId, handleSectionNamingChange,
  handleToggleCollapse, handleRenameSection, handleDeleteSection, handleArchiveSection, handleUnarchiveSection, archiveReopenCounts,
  handleToggleComplete, handleDeleteTask, handleUpdateTask, handleUpdateTaskAndCreateBelow,
  handleDuplicate, handleContextMenuBadgeUpdate, getContextMenuMarks, menuShortcut, onMenuShortcutClose, onPlay,
  optimisticUpdates,
  handleHoverLeave, handleClearSelection,
  sectionsReadonly = false,
  inProjectContext = false,
  completedToggle,
}) => {
  // The per-section window-mode toggle is redundant when the project has a single
  // section — the project-title toggle already scopes the whole project — so only
  // surface it once there are 2+ sections.
  const multiSection = segments.filter((s) => s.section).length > 1
  // Auch beim Ziehen von „Neue Aufgabe“ (#301): Die Lücke schiebt die Zeilen, die Hülle käme nicht mit.
  const sectionHullResting = useRestAfterDrop(!!activeSectionDragId || !!newTaskDrop)
  // Alle Aufgaben des Boards, auch die eingeklappter Abschnitte: Aufklappen lädt dann nichts nach (#303).
  const taskIds = useMemo(() => (tasks ?? []).map(t => t.id), [tasks])
  return (
    <div
      className="project-page__hover-zone"
      onMouseLeave={handleHoverLeave}
      onPointerDown={(e) => {
        const target = e.target as HTMLElement
        // Click on empty space → clear selection.
        // Skip if any dropdown portal is open (Radix or floating-ui based).
        if (document.querySelector('[data-radix-popper-content-wrapper]')) return
        if (document.querySelector('[data-task-dropdown]')) return
        if (
          !target.closest('.task-entry') &&
          !target.closest('.section-header')
        ) {
          handleClearSelection()
        }
      }}
    >
      <SubtaskDragOutContext.Provider value={subtaskDragOut}>
      <NestMenuContext.Provider value={nestMenu}>
      <ListFocusContext.Provider value={navigation.restoreListFocus}>
      <TaskRowDataScope taskIds={taskIds}>
      <LazyRowScope>
      <div className="project-page__content">
        {/* Segmented task list: sections + task groups */}
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={composedDragStart}
          onDragMove={composedDragMove}
          onDragOver={composedDragOver}
          onDragEnd={composedDragEnd}
          onDragCancel={composedDragCancel}
          measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
          autoScroll={{ layoutShiftCompensation: false }}
        >
          <SortableContext items={allSortableIds} strategy={segmentAwareStrategy}>
            <div
              ref={(el) => {
                (taskListRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                (navigation.wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = el
              }}
              className={`project-page__task-list-wrap${activeSectionDragId ? ' project-page__task-list-wrap--section-drag-active' : ''}`}
              tabIndex={-1}
              onKeyDown={navigation.onKeyDown}
              onPointerDownCapture={navigation.onPointerDownCapture}
            >
              {/*
                Eine Auswahl-Hülle über alle Abschnitte: Die Markierung fährt auch von Abschnitt zu
                Abschnitt (#293). Zieht man einen Abschnitt, wandern die Segmente per `transform` —
                auch noch beim Landen (`dropPopMotion`). Das sähe die gemeinsame Ebene nicht, sie
                ruht, und die Zeilen malen sich selbst.
              */}
              <SelectionHullScope resting={sectionHullResting}>
              {/* 'above' TaskEntry rendered outside segments map so segment key changes
                  (unsectioned segment appearing at index 0) don't break the morph transition. */}
              {((creatingSegmentIdx === 0 && creatingPosition === 'above')
                || morphData?.position === 'above') && (
                <motion.div
                  ref={creatingPosition === 'above' ? topCreateRef : undefined}
                  className="project-page__above-create-entry"
                  initial={false}
                  animate={{ height: 'auto' }}
                  transition={{ height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } }}
                  style={{ overflow: 'hidden' }}
                >
                  <TaskEntry
                    key={`create-above-${creationKey}`}
                    mode={morphData?.position === 'above' ? 'collapsed' : 'creating'}
                    task={morphData?.position === 'above' ? { id: 'morphing', title: morphData.title, completed: false } : undefined}
                    selected={morphData?.position === 'above' && morphData.selected}
                    onSubmitCreate={creatingPosition === 'above' ? handleCreateTask : undefined}
                    onSubmitCreateAndContinue={creatingPosition === 'above' ? handleCreateTaskAndContinue : undefined}
                    onCancel={creatingPosition === 'above' ? handleCancelCreate : undefined}
                    autoFocus={creatingPosition === 'above'}
                    onMorphComplete={morphData?.position === 'above' ? handleMorphComplete : undefined}
                  />
                </motion.div>
              )}
              {segments.map((segment, i) => (
                <ProjectSegment
                  key={segment.section?.id ?? `unsectioned-${i}`}
                  segment={segment}
                  index={i}
                  multiSection={multiSection}
                  projectId={projectId}
                  gapAfterSegmentIdx={gapAfterSegmentIdx}
                  gapHeight={gapHeight}
                  activeSectionDragId={activeSectionDragId}
                  dragSourceSegIdx={dragSourceSegIdx}
                  autoEditSectionId={autoEditSectionId}
                  handleSectionNamingChange={handleSectionNamingChange}
                  getSectionProps={navigation.getSectionProps}
                  handleToggleCollapse={handleToggleCollapse}
                  handleRenameSection={handleRenameSection}
                  handleDeleteSection={handleDeleteSection}
                  handleArchiveSection={handleArchiveSection}
                  handleUnarchiveSection={handleUnarchiveSection}
                  archiveReopenCounts={archiveReopenCounts}
                  sectionsReadonly={sectionsReadonly}
                  selectSection={navigation.selectSection}
                  selectSectionTasks={navigation.selectSectionTasks}
                  handleSectionNavigateDown={navigation.handleSectionNavigateDown}
                  handleSectionNavigateUp={navigation.handleSectionNavigateUp}
                  creatingSegmentIdx={creatingSegmentIdx}
                  creatingPosition={creatingPosition}
                  morphData={morphData}
                  positionMorphKey={positionMorphKey}
                  inlineMorphTitle={inlineMorphTitle}
                  inlineMorphSelected={inlineMorphSelected}
                  inlineMorphKey={inlineMorphKey}
                  creationKey={creationKey}
                  creationLanded={creationLanded}
                  newTaskDrop={newTaskDrop}
                  inlineResetKey={inlineResetKey}
                  skipInlineEntrance={skipInlineEntrance}
                  autoSelectId={autoSelectId}
                  creatingAfterTaskId={creatingAfterTaskId}
                  pendingInlineTarget={pendingInlineTarget}
                  topCreateRef={topCreateRef}
                  bottomCreateRef={bottomCreateRef}
                  handleCreateTask={handleCreateTask}
                  handleCreateTaskAndContinue={handleCreateTaskAndContinue}
                  handleCreateTaskAfter={handleCreateTaskAfter}
                  handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
                  handleCancelCreate={handleCancelCreate}
                  handleMorphComplete={handleMorphComplete}
                  handleInlineMorphComplete={handleInlineMorphComplete}
                  handleToggleComplete={handleToggleComplete}
                  handleDeleteTask={handleDeleteTask}
                  handleDuplicate={handleDuplicate}
                  handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
                  getContextMenuMarks={getContextMenuMarks}
                  menuShortcut={menuShortcut}
                  onPlay={onPlay}
                  onMenuShortcutClose={onMenuShortcutClose}
                  handleUpdateTask={handleUpdateTask}
                  handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
                  navigateEdit={navigation.navigateEdit}
                  getExternalSelection={navigation.getExternalSelection}
                  getSegmentHandleRef={getSegmentHandleRef}
                  taskListHandleRef={taskListHandleRef}
                  draggedIds={draggedIds}
                  nestTarget={nestTarget}
                  activeDragId={activeDragId}
                  dragCount={dragCount}
                  isCrossSegmentDrag={isCrossSegmentDrag}
                  optimisticUpdates={optimisticUpdates}
                  setHoveredSegmentIndex={setHoveredSegmentIndex}
                  inProjectContext={inProjectContext}
                />
              ))}
              {/* Drop zone after the last segment */}
              <SectionDropZone index={segments.length} active={!!activeSectionDragId && segments.length !== dragSourceSegIdx + 1} />
              </SelectionHullScope>
            </div>
          </SortableContext>
          {/* Außerhalb der `SortableContext`: Im Haufen wird nicht gezogen —
              eine Ordnung, die man nicht sieht, kann man nicht sortieren
              (ADR 0019). */}
          {completedToggle}
          <DragOverlay dropAnimation={null}>
            <DragOverlayContent
              activeSectionDragId={activeSectionDragId}
              activeDragId={activeDragId}
              segments={segments}
              tasks={tasks}
              sectionDragWidth={sectionDragWidth}
              sectionDragOverlayOffset={sectionDragYOffset}
              dragCount={dragCount}
              dragRotation={dragRotation}
            />
          </DragOverlay>
        </DndContext>
        {insertLine && (
          <div
            className="project-page__insert-line"
            aria-hidden="true"
            // Lage kommt vom Zeiger, bei jeder Bewegung neu — anders als per Inline-Stil nicht zu setzen.
            style={{ top: insertLine.top, left: insertLine.left, width: insertLine.width }}
          />
        )}
      </div>
      </LazyRowScope>
      </TaskRowDataScope>
      </ListFocusContext.Provider>
      </NestMenuContext.Provider>
      </SubtaskDragOutContext.Provider>
    </div>
  )
}
